// src/app/routes/AnnouncementsRoutes.tsx
import React, { useState, useEffect } from 'react';
import { ErrorMessage } from '@hookform/error-message';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  DashboardContainerHeader,
  ScrollContainer,
} from 'app/layout/dashboardLayout/BoxLayout';
import {
  IAnnouncement,
  useCreateAnnouncementMutation,
  useDeleteAnnouncementMutation,
  useLazyGetAnnouncementsQuery,
  useUpdateAnnouncementMutation,
} from 'app/services/announcements';
import { UserRoleEnum } from 'app/services/user';
import {
  AddIcon,
  EditIcon,
  MegaphoneIcon,
  SaveIcon,
  TrashCanSolidIcon,
} from 'assets/icons';
import Button from 'Components/Buttons/Button';
import { USDate } from 'Components/Dates/USDate';
import { TextAreaBasic } from 'Components/Form/Basic/TextAreaBasic';
import { TextInputBasic } from 'Components/Form/Basic/TextInputBasic';
import { SingleDateInput } from 'Components/Form/Inputs/DateInput';
import Label from 'Components/Form/Label';
import { NoResultsFound } from 'Components/NoResultsFound';
import { successToast } from 'Components/Notifications';
import OverlayWindow from 'Components/OverlayWindow';
import usePagination from 'Components/Pagination/usePagination';
import { useClosePopup } from 'Components/Popups';
import { useConfirmDeleteModal } from 'Components/Popups/ConfirmDeletePopup';
import useCreateModal from 'Components/Popups/useCreateModal';

import SelectDropDown, { DDLWrap } from 'Components/SelectDropDown';
import Spinner from 'Components/Spinner';
import moment from 'moment';
import { Controller, useForm } from 'react-hook-form';
import { Route } from 'react-router-dom';
import { useAuth } from 'utils/AuthProvider';
import { ProtectedRoleRoute } from 'utils/RequireAuth';
import * as Yup from 'yup';

/**
 * Replaces any http:// or https:// link in the text with a clickable
 * <a> tag. We remove extra leading/trailing spaces from the URL and
 * use display:inline-block so that only the link text is underlined.
 */
function formatAnnouncementDescription(description: string): string {
  return description.replace(
    /\s*(\bhttps?:\/\/[^\s]+)/gi,
    (fullMatch, rawUrl) => {
      const url = rawUrl.trim(); // remove leading/trailing space from the URL
      return `<a
      href="${url}"
      target="_self"
      rel="noopener noreferrer"
      style="
        color: #39be9d;
        font-weight: bold;
        text-decoration: underline;
        display: inline-block;
        word-break: break-word;
        padding: 2px 4px;
        border-radius: 4px;
        cursor: pointer;
      "
    >${url}</a>`;
    },
  );
}

export const validationShcmena = Yup.object({
  audience: Yup.string().nullable().required('Required'),
  description: Yup.string().trim().nullable().required('Required'),
  started_at: Yup.string().nullable().required('Required'),
  ended_at: Yup.string().nullable(),
  title: Yup.string().trim().nullable().required('Required'),
});

export function AnnouncementForm({
  value,
  onSubmit,
}: {
  value?: IAnnouncement;
  onSubmit: (v: IAnnouncement) => void;
}) {
  const {
    formState: { errors },
    handleSubmit,
    control,
    register,
    watch,
  } = useForm<IAnnouncement>({
    defaultValues: value,
    resolver: yupResolver(validationShcmena),
  });

  const validFrom = watch('started_at');
  const validTo = watch('ended_at');

  return (
    <form
      id="announcement_form"
      className="p-4"
      onSubmit={handleSubmit(onSubmit)}
    >
      <TextInputBasic
        register={register}
        name="title"
        type="text"
        placeholder="Title"
        label="Title"
        errors={errors}
      />

      <TextAreaBasic
        register={register}
        name="description"
        placeholder="Description"
        label="Description"
        errors={errors}
      />

      <Controller
        name="started_at"
        control={control}
        render={({ field }) => (
          <>
            <Label label="Starts at" />
            <SingleDateInput
              minDate={moment().subtract(1, 'days').toDate()}
              maxDate={validTo ? validTo : undefined}
              onChangeStringDate={field.onChange}
              value={validFrom}
              hourOffset="min"
              format="UTC"
            />
            <ErrorMessage
              errors={errors}
              name="started_at"
              render={({ message }) => (
                <p className="text-red-dark text-xs whitespace-pre-line bg-red-100 rounded-lg w-full p-1 mt-2 pl-4">
                  {message}
                </p>
              )}
            />
          </>
        )}
      />

      <Controller
        name="ended_at"
        control={control}
        render={({ field }) => (
          <>
            <Label label="Ends at" />
            <SingleDateInput
              minDate={validFrom ? validFrom : undefined}
              onChangeStringDate={(v) =>
                v ? field.onChange(v) : field.onChange(null)
              }
              value={validTo}
              hourOffset="max"
              format="UTC"
            />
            <ErrorMessage
              errors={errors}
              name="ended_at"
              render={({ message }) => (
                <p className="text-red-dark text-xs whitespace-pre-line bg-red-100 rounded-lg w-full p-1 mt-2 capitalize pl-4">
                  {message}
                </p>
              )}
            />
          </>
        )}
      />

      <Controller
        name="audience"
        control={control}
        render={({ field }) => (
          <>
            <Label label="Audience" />
            <SelectDropDown
              onChange={field.onChange}
              value={field.value}
              options={DDLWrap(['all', 'accountants', 'clients'])}
            />
            <ErrorMessage
              errors={errors}
              name="audience"
              render={({ message }) => (
                <p className="text-red-dark text-xs whitespace-pre-line bg-red-100 rounded-lg w-full p-1 mt-2 capitalize pl-4">
                  {message}
                </p>
              )}
            />
          </>
        )}
      />
    </form>
  );
}

export function CreateAnnouncementForm({ onCreate }: { onCreate: () => void }) {
  const [Create, { isLoading }] = useCreateAnnouncementMutation();
  const closePopup = useClosePopup();

  const onSubmit = (form: IAnnouncement) => {
    Create(form).unwrap().then(onCreate);
  };

  return (
    <>
      <DashboardContainerHeader title="Create New Announcement"></DashboardContainerHeader>
      <AnnouncementForm onSubmit={onSubmit} />
      <div className="flex gap-4 justify-end p-4">
        <Button type="button" className="max-w-min" onClick={closePopup}>
          Cancel
        </Button>
        <Button
          type="submit"
          className="max-w-min"
          isLoading={isLoading}
          form="announcement_form"
        >
          <SaveIcon className="w-4 h-4" /> Create
        </Button>
      </div>
    </>
  );
}

export function EditAnnouncementForm({
  onUpdate,
  value,
}: {
  onUpdate: () => void;
  value: IAnnouncement;
}) {
  const [Update, { isLoading }] = useUpdateAnnouncementMutation();

  const onSubmit = (form: IAnnouncement) => {
    Update(form).unwrap().then(onUpdate);
  };

  const closePopup = useClosePopup();

  return (
    <>
      <DashboardContainerHeader title="Create New Announcement"></DashboardContainerHeader>
      <AnnouncementForm value={value} onSubmit={onSubmit} />
      <div className="flex gap-4 justify-end p-4">
        <Button type="button" className="max-w-min" onClick={closePopup}>
          Cancel
        </Button>
        <Button
          type="submit"
          className="max-w-min"
          isLoading={isLoading}
          form="announcement_form"
        >
          <SaveIcon className="w-4 h-4" /> Update
        </Button>
      </div>
    </>
  );
}

function AnnoucementDateStatus({ value }: { value: IAnnouncement }) {
  const nowTimestamp = new Date().toISOString();
  const startedTimeStamp = new Date(value.started_at).toISOString();
  const endedTimeStamp = new Date(value.ended_at).toISOString();

  const isPending = startedTimeStamp > nowTimestamp;
  const isExpired = value.ended_at && endedTimeStamp < nowTimestamp;

  if (isPending) return <div className="bg-[#fae9a2] w-2 h-2 rounded-full" />;
  if (isExpired) return <div className="bg-red-200 w-2 h-2 rounded-full" />;
  return <div className="bg-green w-2 h-2 rounded-full border-gray-100" />;
}

export function AnnoucementAdminCard({
  value,
  onEditClick,
  onDeleteClick,
}: {
  value: IAnnouncement;
  onDeleteClick: (value: IAnnouncement) => void;
  onEditClick: (value: IAnnouncement) => void;
}) {
  const { isAdmin } = useAuth();
  const nowTimestamp = new Date().toISOString();
  const startedTimeStamp = new Date(value.started_at).toISOString();
  const endedTimeStamp = new Date(value.ended_at).toISOString();

  const isPending = startedTimeStamp > nowTimestamp;
  const isExpired = value.ended_at && endedTimeStamp < nowTimestamp;

  const audience =
    value.audience === 'all' ? ['Accountants', 'Clients'] : [value.audience];

  return (
    <div className="grid p-4 border-gray-100 border-b">
      <div className="flex w-full gap-4 pb-2 items-center capitalize">
        <div className="text-gray-dark font-semibold">{value.title}</div>
        <Button
          className="ml-auto max-w-min"
          onClick={() => onEditClick(value)}
        >
          <EditIcon />
        </Button>
        <Button
          className="max-w-min"
          theme="red-outline"
          onClick={() => onDeleteClick(value)}
        >
          <TrashCanSolidIcon className="text-red-200" />
        </Button>
      </div>

      {/* Render the link with break-words to avoid horizontal scroll */}
      <div
        className="pl-2 border-l-4 border-gray-100 text-gray-700 whitespace-pre-wrap break-words"
        dangerouslySetInnerHTML={{
          __html: formatAnnouncementDescription(value.description),
        }}
      />

      <div className="flex items-center mt-4 gap-2">
        <div className="bg-gray-400 shadow flex gap-1 overflow-hidden rounded text-gray-700 text-sm capitalize">
          <div
            className={`w-4 ${
              isPending ? 'bg-[#f6e9b7]' : isExpired ? 'bg-red-200' : 'bg-green'
            }`}
          />
          <span className="py-0.5 px-1 flex items-center">
            <USDate date={value.started_at} />
            {value.ended_at && (
              <>
                &nbsp;-&nbsp;
                <USDate date={value.ended_at} />
              </>
            )}
          </span>
        </div>

        {audience.map((target) => (
          <div
            key={target}
            className="bg-gray-400 shadow px-1 py-0.5 rounded text-gray-700 text-sm capitalize"
          >
            {target}
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminAnnouncementsList() {
  const { isAdmin } = useAuth();

  const {
    data,
    Pagination,
    refetchSamePage,
    goToFirstPage,
    refetchAfterDelete,
  } = usePagination(useLazyGetAnnouncementsQuery, {
    filters: {},
    per_page: isAdmin ? 10 : 1,
  });
  const AnnouncementFormModal = useCreateModal<IAnnouncement>();

  const confrimDelete = useConfirmDeleteModal();
  const [Delete] = useDeleteAnnouncementMutation();

  const onCreate = () => {
    successToast('Announcement Created');
    goToFirstPage();
    AnnouncementFormModal.close();
  };

  const onUpdate = () => {
    successToast('Announcement Updated');
    refetchSamePage();
    AnnouncementFormModal.close();
  };

  const onEditClick = (data: IAnnouncement) => {
    AnnouncementFormModal.openWithContext({ data });
  };

  const onDeleteClick = (data: IAnnouncement) => {
    confrimDelete('Announcement', () =>
      Delete(data.id).unwrap().then(refetchAfterDelete),
    );
  };

  if (!data) return <Spinner />;

  return (
    <ScrollContainer>
      <DashboardContainerHeader title="Announcements">
        <Button
          onClick={() => AnnouncementFormModal.openWithContext({})}
          className="max-w-min"
        >
          <AddIcon className="w-3 h-3" />
          Add
        </Button>
      </DashboardContainerHeader>

      <ScrollContainer>
        {data?.length === 0 && <NoResultsFound title="No items found" />}
        {data?.map((item) => (
          <AnnoucementAdminCard
            key={item.id}
            onDeleteClick={onDeleteClick}
            onEditClick={onEditClick}
            value={item}
          />
        ))}
      </ScrollContainer>
      <Pagination />

      <AnnouncementFormModal.Component>
        {({ data }) => (
          <OverlayWindow paddingSize="" onClose={AnnouncementFormModal.close}>
            {!data?.id && <CreateAnnouncementForm onCreate={onCreate} />}
            {!!data?.id && (
              <EditAnnouncementForm value={data} onUpdate={onUpdate} />
            )}
          </OverlayWindow>
        )}
      </AnnouncementFormModal.Component>
    </ScrollContainer>
  );
}

export function ViewAnnouncements({ onLoad }: { onLoad: () => void }) {
  const { data, page, totalCount, GetPage, pagination, isLoading } =
    usePagination(
      useLazyGetAnnouncementsQuery,
      {
        filters: {},
        per_page: 1,
      },
      {
        skipQueryParamsCache: true,
      },
    );

  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      onLoad();
    }
  }, [isLoading, onLoad]);

  if (!data) return null;

  return (
    <ScrollContainer className="border border-gray-100 rounded-md shadow w-full h-full">
      <div className="border-gray-100 border-b p-2 flex gap-2 text-sm items-center text-gray-dark font-semibold">
        <MegaphoneIcon className="text-green w-4 h-4" /> Announcements
        {pagination.count > 0 && (
          <>
            <Button
              className="ml-auto max-w-min text-medium"
              disabled={page === 1}
              onClick={() => GetPage(page - 1)}
            >
              {'<'}
            </Button>
            <span className="text-green flex items-center gap-2">
              {page} of {pagination.count}
            </span>
            <Button
              className="max-w-min text-medium"
              disabled={page === totalCount}
              onClick={() => GetPage(page + 1)}
            >
              {'>'}
            </Button>
          </>
        )}
        <Button
          className="ml-1 p-1 max-w-min text-small"
          onClick={() => setShowHistory(true)}
        >
          View All
        </Button>
      </div>
      <ScrollContainer>
        {data.length === 0 && (
          <NoResultsFound
            className="text-gray-light"
            Icon={<MegaphoneIcon className="w-10 h-10" />}
            subTitle="Announcements will appear here."
          />
        )}
        {data?.map((item) => (
          <div key={item.id} className="grid gap-2 flex-1 p-4 content-start">
            <div className="flex w-full items-center font-semibold">
              <div className="flex-1 text-gray-dark text-sm">{item.title}</div>
            </div>
            <div
              className="text-gray-700 whitespace-pre-wrap text-sm break-words"
              dangerouslySetInnerHTML={{
                __html: formatAnnouncementDescription(item.description),
              }}
            />
          </div>
        ))}
      </ScrollContainer>

      {showHistory && (
        <AnnouncementsHistoryPopup onClose={() => setShowHistory(false)} />
      )}
    </ScrollContainer>
  );
}

const AnnouncementsHistoryPopup = ({ onClose }: { onClose: () => void }) => {
  const [getAnnouncements, { isLoading }] = useLazyGetAnnouncementsQuery();
  const [allAnnouncements, setAllAnnouncements] = useState<IAnnouncement[]>([]);
  const [hasFetched, setHasFetched] = useState(false);

  useEffect(() => {
    if (!hasFetched) {
      const fetchAllAnnouncements = async () => {
        let allData: IAnnouncement[] = [];
        let page = 1;
        let totalPages = 1;

        while (page <= totalPages) {
          const response = await getAnnouncements({
            filters: {},
            page,
          }).unwrap();
          allData = [...allData, ...response.data];
          totalPages = response.pagination.pages;
          page += 1;
        }

        setAllAnnouncements(allData);
        setHasFetched(true);
      };

      fetchAllAnnouncements();
    }
  }, [getAnnouncements, hasFetched]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="relative w-full max-w-4xl mx-auto p-4 bg-white rounded-lg shadow-lg max-h-[85vh]">
        <button
          className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-2xl font-bold"
          onClick={onClose}
        >
          &times;
        </button>
        <h2 className="text-2xl font-bold mb-2 text-center">
          Announcements History
        </h2>
        {isLoading ? (
          <Spinner />
        ) : (
          <div className="overflow-y-auto max-h-[70vh] px-4">
            {allAnnouncements.map((item) => (
              <div key={item.id} className="mb-4 border-b pb-2">
                <h3 className="font-semibold">{item.title}</h3>
                <p
                  className="text-gray-600 break-words"
                  dangerouslySetInnerHTML={{
                    __html: formatAnnouncementDescription(item.description),
                  }}
                />
                <p className="text-gray-500 text-sm">
                  {item.started_at} - {item.ended_at}
                </p>
              </div>
            ))}
          </div>
        )}
        <div className="flex justify-center mt-2">
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
};

export default function AnnouncementsRoutes() {
  return (
    <>
      <Route
        path="announcements"
        element={<ProtectedRoleRoute roles={[UserRoleEnum.ADMIN]} />}
      >
        <Route path="" element={<AdminAnnouncementsList />} />
      </Route>
    </>
  );
}
